import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import AgreementFactsTimeline from '../components/verification/AgreementFactsTimeline';
import DeliveryStatusCards from '../components/verification/DeliveryStatusCards';
import EvidenceSurface from '../components/verification/EvidenceSurface';
import ProgressUpdates from '../components/verification/ProgressUpdates';
import VerificationActions from '../components/agreementDetail/VerificationActions';
import { pnlApi, verificationRunsApi } from '../utils/api';
import CheckInProgress from '../components/verification/CheckInProgress';

// PRD-031 F6 — the Delivery sub-page (customer word: "Delivery", never
// "verification"), one per Agreement, built to the ratified
// mockup-verification-subpage-prd031.html. The Agreement page stays the P&L
// pane; everything verification-detailed lives here: the facts + timeline
// strip (relocated per F6), the three tabbed surfaces (Delivery status /
// Progress / Evidence — content unchanged from PRD-030 Phase 2), and every
// verification ACTION (Submit evidence, manual confirmation, the Client's
// accept / send-back) under its Outcome's status card (F6.3). Both roles
// see the page (full visibility, PRD-030 F7); deep links `?tab=` and
// `?outcome=` keep working (F6.1).

const NON_TERMINAL_RUN_STATUSES = ['queued', 'planning', 'collecting', 'judging'];
const isRunNonTerminal = (r) => NON_TERMINAL_RUN_STATUSES.includes(r.status);

function toRows(pnl) {
  return (pnl.outcomes || []).map((o) => ({
    buyerSignoff: o.buyer_signoff,
    refuteNote: o.refute_note,
    outcomeId: o.outcome_id,
    name: o.name,
    status: o.status,
    revenueCents: o.revenue_cents,
    sellUnitName: o.sell_unit_name || '',
    sellUnitNoun: o.sell_unit_noun || '',
    activeFrom: o.active_from || null,
    activeTo: o.active_to || null,
    sellQuantity: o.sell_quantity != null ? o.sell_quantity : null,
    evidence: o.evidence || [],
    deliverables: o.deliverables || [],
    delivery: o.delivery || null,
    verifiedAt: o.verified_at,
    acceptedAt: o.accepted_at,
  }));
}

export default function DeliverySubpage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPnl = useCallback(async () => {
    try {
      const res = await pnlApi.get(id);
      setPnl(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { fetchPnl(); }, [fetchPnl]);

  // PRD-031 F2.1 — live run status: poll the ledger while a run is
  // non-terminal; refetch the P&L the moment one finishes.
  const [runs, setRuns] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const [runAllBusy, setRunAllBusy] = useState(false);
  const [runAllResult, setRunAllResult] = useState(null);
  const runsRef = useRef([]);
  const fetchRuns = useCallback(async () => {
    try {
      const res = await verificationRunsApi.list(id);
      const next = res.data.runs || [];
      const prevNonTerminal = new Set(runsRef.current.filter(isRunNonTerminal).map((r) => r.id));
      const finishedNow = next.some((r) => prevNonTerminal.has(r.id) && !isRunNonTerminal(r));
      runsRef.current = next;
      setRuns(next);
      if (finishedNow) fetchPnl();
    } catch (err) { /* transient — the next poll retries */ }
  }, [id, fetchPnl]);
  useEffect(() => { fetchRuns(); }, [fetchRuns]);
  const anyRunLive = runs.some(isRunNonTerminal);
  useEffect(() => {
    if (!anyRunLive) return undefined;
    const t = setInterval(fetchRuns, 5000);
    return () => clearInterval(t);
  }, [anyRunLive, fetchRuns]);

  // Tabs sync with ?tab= so email CTAs and attention items land right.
  const isAgency = pnl?.role === 'seller';
  const tabs = [
    { key: 'status', label: 'Delivery status' },
    { key: 'progress', label: isAgency ? 'Progress & updates' : 'Progress updates' },
    { key: 'evidence', label: 'Evidence' },
  ];
  const requested = searchParams.get('tab');
  const tab = tabs.some((t) => t.key === requested) ? requested : 'status';
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    next.delete('outcome');
    setSearchParams(next, { replace: true });
  };

  // Outcome anchor (?outcome=<id>): scroll + flash the card once rendered.
  const anchored = useRef(false);
  const outcomeParam = searchParams.get('outcome');
  useEffect(() => {
    if (anchored.current || !outcomeParam || !pnl || tab !== 'status') return;
    const el = document.getElementById(`delivery-outcome-${outcomeParam}`);
    if (el) {
      anchored.current = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('prd010-flash');
      setTimeout(() => el.classList.remove('prd010-flash'), 1900);
    }
  });

  if (loading) return <Layout><div className="max-w-4xl mx-auto card p-6 animate-pulse h-40" /></Layout>;
  if (error === 'not_found') return <Layout><div className="max-w-4xl mx-auto card p-8 text-center text-gray-600">Agreement not found. <button className="text-primary-600" onClick={() => navigate('/agreements')}>Back to Agreements</button></div></Layout>;
  if (error || !pnl) return <Layout><div className="max-w-4xl mx-auto card p-8 text-center text-gray-600">Failed to load this Agreement.</div></Layout>;

  const rows = toRows(pnl);
  // A stalled check needs a way out, not just a sentence saying it is slow.
  // Only the agency can spend a run. A button that can 403 or 500 has to
  // say so: an unhandled rejection out of onClick shows the user nothing,
  // which is the same silence this whole work unit is closing.
  const retryStalledCheck = async () => {
    setRetryError('');
    setRetrying(true);
    try {
      await verificationRunsApi.trigger(id, { deliverable_id: liveRun?.deliverable_id || undefined });
      await fetchRuns();
    } catch (err) {
      // Server message first, human sentence last. axios always populates
      // err.message, so leading with it shipped "Request failed with status
      // code 403" to a customer — the repo's other callers all invert this.
      setRetryError(
        err?.response?.data?.message
        || err?.response?.data?.error
        || "Couldn't start another check. Try again."
      );
    } finally {
      setRetrying(false);
    }
  };
  const SKIP_STATUSES = new Set(['delivered_confirmed', 'delivered_pending_confirmation', 'checking_now']);
  const runAllChecks = async () => {
    setRunAllBusy(true);
    setRunAllResult(null);
    let queued = 0;
    try {
      for (const row of rows) {
        const status = row.delivery?.status;
        if (!status || SKIP_STATUSES.has(status)) continue;
        const hasEvidence = row.delivery?.evidence_present || (row.evidence || []).length > 0;
        if (!hasEvidence) continue;
        const d = (row.deliverables || [])[0];
        if (!d) continue;
        await verificationRunsApi.trigger(id, { deliverable_id: d.id });
        queued += 1;
      }
      setRunAllResult(queued > 0 ? `${queued} check${queued === 1 ? '' : 's'} queued.` : 'No outcomes are ready to check.');
      await fetchRuns();
    } catch (err) {
      setRunAllResult(err?.response?.data?.error || "Couldn't queue all checks — try again.");
    } finally {
      setRunAllBusy(false);
    }
  };

  // Newest non-terminal, matching VerificationActions. Selecting only
  // in-flight stages skipped the `queued` run a Retry creates, so the widget
  // kept showing the run the user had just abandoned.
  const liveRun = [...runs]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .find((r) => ['planning', 'collecting', 'judging'].includes(r.status)) || null;
  const newerQueuedRun = liveRun
    ? runs.some((r) => r.status === 'queued' && new Date(r.created_at) > new Date(liveRun.created_at))
    : false;
  const liveRunOutcomeName = liveRun && liveRun.deliverable_id
    ? (rows.find((r) => (r.deliverables || []).some((d) => d.id === liveRun.deliverable_id)) || {}).name || null
    : null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4">
        <div>
          <div className="text-[12.5px] text-gray-600">
            <Link to="/agreements" className="hover:text-gray-900">Agreements</Link>
            {' › '}<b className="text-gray-700 font-semibold">{pnl.name}</b>
            {' › '}<Link to={`/agreements/${id}`} className="text-primary-600 font-semibold">‹ Back to agreement</Link>
          </div>
          <h1 className="page-header mt-0.5">Delivery</h1>
        </div>

        {/* The Agreement facts + timeline strip — relocated here (F6). */}
        <AgreementFactsTimeline pnl={pnl} />

        <div className="flex gap-1.5 flex-wrap items-center">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`text-[12.5px] font-semibold px-3.5 py-2 rounded-lg border ${
                tab === t.key
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {t.label}
            </button>
          ))}
          {isAgency && (
            <>
              <span className="flex-1" />
              <button
                type="button"
                disabled={runAllBusy}
                onClick={runAllChecks}
                className="text-[12.5px] font-semibold px-3.5 py-2 rounded-lg border bg-primary-600 text-white border-primary-600 hover:bg-primary-700 disabled:opacity-70"
              >
                {runAllBusy ? 'Queuing…' : 'Run all checks'}
              </button>
            </>
          )}
        </div>
        {runAllResult && (
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700">
            <span>{runAllResult}</span>
            <button type="button" onClick={() => setRunAllResult(null)} className="text-[11.5px] font-semibold text-gray-600">Dismiss</button>
          </div>
        )}

        {/* WU-F: the running check sits ABOVE the tabs, not inside one.
            Mounted per-tab it reached the agency on Evidence and left the
            client — who lands on the status tab — with a static sentence,
            which is the silence RJ asked to fix (QA finding B3). */}
        {/* A queued run created after this one means the user already asked
            for another check; the countdown below owns that story. */}
        <CheckInProgress
          run={newerQueuedRun ? null : liveRun}
          outcomeName={liveRunOutcomeName}
          onRetry={isAgency ? retryStalledCheck : null}
          retrying={retrying}
          retryError={retryError}
        />

        {tab === 'status' && (
          <DeliveryStatusCards
            agreementId={id}
            rows={rows}
            isAgency={isAgency}
            onEvidenceAdded={async () => { await fetchRuns(); await fetchPnl(); }}
            renderActions={(row, opts = {}) => (
              <VerificationActions
                row={row}
                isSeller={isAgency}
                agreementId={id}
                sellerName={pnl.parties?.seller?.name}
                buyerName={pnl.parties?.buyer?.name}
                onChanged={fetchPnl}
                runs={runs}
                onRunsChanged={fetchRuns}
                evidenceCtaShown={opts.evidenceCtaShown === true}
              />
            )}
          />
        )}
        {tab === 'progress' && (
          <ProgressUpdates
            agreementId={id}
            rows={rows}
            isAgency={isAgency}
            clientName={pnl.parties?.buyer?.name}
            onOpenStatus={() => setTab('status')}
          />
        )}
        {tab === 'evidence' && (
          <EvidenceSurface agreementId={id} outcomes={rows} isAgency={isAgency} />
        )}
      </div>
    </Layout>
  );
}
